/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import flow from 'lodash.flow';
import * as React from 'react';
import { useAuthKeys } from 'src/core/hooks/useAuthKeys';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { addToast } from 'src/core/utils/decorators/addToast';
import { addTracker } from 'src/core/utils/decorators/addTracker';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { commonDemosMessages, resourceEndpointCurlPlaceholder } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { textAnalyticsDemosSupportedRegion } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import {
    AnalyzeSentimentsDemoSamplesJsonOutput,
    DetectLanguageDemoSamplesJsonOutput,
    HealthcareEntitiesDemoSamplesFHIRBundle,
    HealthcareEntitiesDemoSamplesJsonOutput,
    KeyPhrasesDemoSamplesJsonOutput,
    LinkedEntitiesDemoSamplesJsonOutput,
    NamedEntityRecognitionDemoSamplesJsonOutput,
    PIIDemoSamplesJsonOutput,
    TextSummarizationDemoSamplesJsonOutput
} from 'src/languageStudio/appShell/prebuiltDemos/utils/signedOutMockedSamplesJsonOutput';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { TryoutModelVersion } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedModelVerion';
import { getTextAnalyticsService } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsService';
import {
    DetectedLanguagesDocuments,
    DocumentHealthcareEntitiesSwagger,
    InputDocument,
    InputDocumentLanguage,
    KeyPhrasesDocuments,
    LinkedEntitiesDocuments,
    NamedEntityRecognitionDocuments,
    NewSummarizationTaskSwagger,
    PIIDocuments,
    SentimentDocuments,
    SummarizationSwagger,
    SummarizationTask,
    SummarizationTaskType,
    TextAnalyticsResultErrorsList
} from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export type SummarizationResult = {
    summarizationDemoOutput?: SummarizationTask<SummarizationSwagger>[];
    extractive?: SummarizationSwagger & { status: SummarizationTask<SummarizationSwagger>['status'] };
    abstractive: SummarizationSwagger & { status: SummarizationTask<SummarizationSwagger>['status'] };
};

export const useTextAnalyticsCapabilities = () => {
    const tracker = useProgressTracker();
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const { isSignedIn } = rootStore.authStore;
    const textAnalyticsService = getTextAnalyticsService();
    const currentServiceKey = rootStore.azureStore.session.serviceKey;
    const azureResource = rootStore.azureStore.session.csAccountStore;

    const [piiResult, setPiiResult] = React.useState<PIIDocuments>();
    const [sentiment, setSentiment] = React.useState<SentimentDocuments>();
    const [keyPhrases, setKeyPhrases] = React.useState<KeyPhrasesDocuments>();
    const [summarization, setSummarization] = React.useState<SummarizationResult>();
    const [linkedEntities, setLinkedEntities] = React.useState<LinkedEntitiesDocuments>();
    const [namedEntities, setNamedEntities] = React.useState<NamedEntityRecognitionDocuments>();
    const [detectedLanguages, setDetectedLanguages] = React.useState<DetectedLanguagesDocuments>();
    const [healthAnalysis, setHealthAnalysis] = React.useState<DocumentHealthcareEntitiesSwagger>();

    const resetResults = () => {
        tracker.reset();
        setSentiment(null);
        setPiiResult(null);
        setKeyPhrases(null);
        setNamedEntities(null);
        setSummarization(null);
        setLinkedEntities(null);
        setHealthAnalysis(null);
        setDetectedLanguages(null);
    };

    const { authorizationHeader } = useAuthKeys({ currentServiceKey, currentResource: azureResource });

    const preValidate = (
        language: boolean,
        input: boolean,
        resourceExtraInvalidator: (resource: AzureCsAccountStore) => boolean = () => false,
        invalidResourceErrorMessage: string = localize(commonDemosMessages.azureResourceInvalidRegionAndSkuErrorMessage)
    ) => {
        let error: Error;
        const isInvalidInput = !input;
        const isInvalidLanguage = !language;
        const isInvalidResource =
            textAnalyticsDemosSupportedRegion.every(r => r !== azureResource?.region) || resourceExtraInvalidator(azureResource);

        if (isInvalidResource) {
            error = new Error(invalidResourceErrorMessage);
            error.name = localize(commonDemosMessages.azureResourceErrorTitle);
        } else if (isInvalidLanguage) {
            error = new Error(localize(commonDemosMessages.languageErrorMessage));
            error.name = localize(commonDemosMessages.languageErrorTitle);
        } else if (isInvalidInput) {
            error = new Error(localize(commonDemosMessages.inputErrorMessage));
            error.name = localize(commonDemosMessages.inputErrorTitle);
        }

        if (isInvalidInput || isInvalidLanguage || isInvalidResource) {
            const inputStep = document.querySelector('#demoPageDocumentInputStep');
            inputStep?.scrollIntoView({ behavior: 'smooth' });

            throw error;
        }
    };

    const postValidate = <T extends TextAnalyticsResultErrorsList>(response: T) => {
        const { errors } = response;
        if (errors?.length) {
            const e = new Error();
            e.name = errors[0].error.code;
            e.message = errors[0].error.message;

            throw e;
        }
    };

    const getCurlCommand = ({
        text,
        endpoint,
        language,
        parameters,
        lro = false,
        apiVersion,
        languageAutoDetection
    }: {
        text: string;
        lro?: boolean;
        endpoint: string;
        apiVersion?: string;
        language?: Language['code'];
        parameters?: object;
        languageAutoDetection?: boolean;
    }): string => {
        let payload: InputDocument | InputDocumentLanguage = {
            id: 'documentId',
            text: text ?? localize(commonDemosMessages.textCurlPlaceholder)
        };

        if (endpoint !== 'languages') {
            const isLanguageAvailable = Boolean(
                languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language
            );

            payload = {
                ...payload,
                ...(isLanguageAvailable && {
                    language: languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language
                })
            };
        }

        return `@ECHO OFF

  curl -v -X POST "${azureResource?.endpoint ?? resourceEndpointCurlPlaceholder}language/${
            lro ? 'analyze-text/jobs' : ':analyze-text'
        }?api-version=${apiVersion ?? LuisConstants.Demos.ApiVersions.GA}" -H "Content-Type: application/json" -H "${
            authorizationHeader.key
        }: ${authorizationHeader.value}" --data-ascii "${JSON.stringify({
            ...(lro ? { tasks: [{ kind: endpoint }] } : { kind: endpoint }),
            analysisInput: { documents: [payload] },
            ...(parameters && { parameters })
        })
            .split('"')
            .join('\\"')}" `;
    };

    const getSummarizationCurlCommand = ({
        text,
        endpoint,
        language,
        sentenceCount = 3,
        query = '',
        languageAutoDetection
    }: {
        text: string;
        endpoint: string;
        sentenceCount?: number;
        query?: string;
        language?: Language['code'];
        languageAutoDetection?: boolean;
    }): string => {
        let payload: InputDocument | InputDocumentLanguage = {
            id: 'documentId',
            text: text ?? localize(commonDemosMessages.textCurlPlaceholder)
        };

        const isLanguageAvailable = Boolean(
            languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language
        );

        payload = {
            ...payload,
            ...(isLanguageAvailable && {
                language: languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language
            })
        };

        return `@ECHO OFF

 curl -v -X POST "${azureResource?.endpoint ??
            resourceEndpointCurlPlaceholder}language/analyze-text/jobs?api-version=${LuisConstants.Demos.SummarizationApiVersion}" -H "Content - Type: application / json" -H "${
            authorizationHeader.key
        }: ${authorizationHeader.value}" --data-ascii "${JSON.stringify({
            analysisInput: { documents: [payload] },
            tasks: [{ kind: endpoint, parameters: { sentenceCount, query } }]
        })
            .split('"')
            .join('\\"')}" `;
    };

    const getNamedEntities = ({
        document,
        language,
        sampleTitle,
        modelVersion,
        autoDetectLanguage
    }: {
        document: string;
        sampleTitle?: string;
        language?: Language['code'];
        autoDetectLanguage?: boolean;
        modelVersion?: TryoutModelVersion;
    }) =>
        !isSignedIn
            ? flow(() => setNamedEntities(NamedEntityRecognitionDemoSamplesJsonOutput[sampleTitle]))()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      const namedEntitiesResponse = await textAnalyticsService.getNamedEntityRecognition({
                          modelVersion,
                          documents: [{ id: getId(), text: document, language: documentLanguage }]
                      });

                      postValidate(namedEntitiesResponse);

                      setNamedEntities(namedEntitiesResponse);
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const getLinkedEntities = ({
        document,
        language,
        sampleTitle,
        autoDetectLanguage
    }: {
        document: string;
        sampleTitle?: string;
        language?: Language['code'];
        autoDetectLanguage?: boolean;
    }) =>
        !isSignedIn
            ? flow(() => setLinkedEntities(LinkedEntitiesDemoSamplesJsonOutput[sampleTitle]))()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      const linkedEntitiesReponse = await textAnalyticsService.getLinkedEntities({
                          documents: [{ id: getId(), text: document, language: documentLanguage }]
                      });

                      postValidate(linkedEntitiesReponse);

                      setLinkedEntities(linkedEntitiesReponse);
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const detectLanguage = ({ document, countryHint, sampleTitle }: { document: string; countryHint?: string; sampleTitle?: string }) =>
        !isSignedIn
            ? flow(() => setDetectedLanguages(DetectLanguageDemoSamplesJsonOutput[sampleTitle]))()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(true, Boolean(document));

                      setDetectedLanguages(
                          await textAnalyticsService.detectLanguage({ documents: [{ id: getId(), text: document, countryHint }] })
                      );
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const getSentiment = ({
        document,
        language,
        sampleTitle,
        withAssessmentMining,
        autoDetectLanguage
    }: {
        document: string;
        sampleTitle?: string;
        language?: Language['code'];
        withAssessmentMining?: boolean;
        autoDetectLanguage?: boolean;
    }) =>
        !isSignedIn
            ? flow(() =>
                  setSentiment(
                      withAssessmentMining
                          ? AnalyzeSentimentsDemoSamplesJsonOutput[sampleTitle].OpinionOn
                          : AnalyzeSentimentsDemoSamplesJsonOutput[sampleTitle].OpinionOff
                  )
              )()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      const sentimentResponse = await textAnalyticsService.getSentiment({
                          documents: [{ id: getId(), text: document, language: documentLanguage }],
                          withAssessmentMining
                      });

                      postValidate(sentimentResponse);

                      setSentiment(sentimentResponse);
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const getKeyPhrases = ({
        document,
        language,
        sampleTitle,
        autoDetectLanguage
    }: {
        document: string;
        sampleTitle?: string;
        language?: Language['code'];
        autoDetectLanguage?: boolean;
    }) =>
        !isSignedIn
            ? flow(() => setKeyPhrases(KeyPhrasesDemoSamplesJsonOutput[sampleTitle]))()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }
                      const keyPhrasesResponse = await textAnalyticsService.getKeyPhrases({
                          documents: [{ id: getId(), text: document, language: documentLanguage }]
                      });

                      postValidate(keyPhrasesResponse);

                      setKeyPhrases(keyPhrasesResponse);
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const getPII = ({
        withPhi,
        document,
        language,
        sampleTitle,
        autoDetectLanguage
    }: {
        document: string;
        withPhi?: boolean;
        sampleTitle?: string;
        language?: Language['code'];
        autoDetectLanguage?: boolean;
    }) =>
        !isSignedIn
            ? flow(() =>
                  setPiiResult(withPhi ? PIIDemoSamplesJsonOutput[sampleTitle].PHIOn : PIIDemoSamplesJsonOutput[sampleTitle].PHIOff)
              )()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      const piiResponse = await textAnalyticsService.getPII({
                          withPhi,
                          documents: [{ id: getId(), text: document, language: documentLanguage }]
                      });

                      postValidate(piiResponse);

                      setPiiResult(piiResponse);
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const getHealthAnalysis = ({
        document,
        sampleTitle,
        apiVersion,
        modelVersion,
        fhirStructure,
        language,
        autoDetectLanguage
    }: {
        document: string;
        apiVersion: string;
        modelVersion: string;
        fhirStructure: boolean;
        autoDetectLanguage?: boolean;
        language?: Language['code'];
        sampleTitle?: string;
    }) =>
        !isSignedIn
            ? flow(() => {
                  const healthAnalysisResult: DocumentHealthcareEntitiesSwagger = HealthcareEntitiesDemoSamplesJsonOutput[sampleTitle];

                  if (fhirStructure) {
                      healthAnalysisResult.documents[0].fhirBundle = HealthcareEntitiesDemoSamplesFHIRBundle[sampleTitle];
                  }

                  setHealthAnalysis(healthAnalysisResult);
              })()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document), r => r.sku.code !== 'S');
                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });
                          setDetectedLanguages(languageResult);
                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      const finalResults = await textAnalyticsService.getHealthAnalysis({
                          modelVersion,
                          apiVersion,
                          fhirStructure,
                          documents: [{ id: getId(), text: document, language: documentLanguage }]
                      });
                      postValidate(finalResults.tasks.items[0].results);
                      setHealthAnalysis(finalResults.tasks.items[0].results);
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const getSummarization = ({
        tasks,
        document,
        language,
        sampleTitle,
        sentenceCount,
        query,
        autoDetectLanguage
    }: {
        document: string;
        sampleTitle?: string;
        sentenceCount?: number;
        query?: string;
        language?: Language['code'];
        autoDetectLanguage?: boolean;
        tasks: SummarizationTaskType[];
    }) =>
        !isSignedIn
            ? flow(() =>
                  setSummarization({
                      extractive: tasks.includes('ExtractiveSummarization') && {
                          ...TextSummarizationDemoSamplesJsonOutput[sampleTitle][1].results,
                          status: 'succeeded'
                      },
                      abstractive: tasks.includes('AbstractiveSummarization') && {
                          ...TextSummarizationDemoSamplesJsonOutput[sampleTitle][0].results,
                          status: 'succeeded'
                      },
                      summarizationDemoOutput: TextSummarizationDemoSamplesJsonOutput[sampleTitle].filter(output =>
                          tasks.includes(output.kind)
                      )
                  })
              )()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(document));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document.trim().slice(0, document.length > 5000 ? 5000 : document.length) }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      await textAnalyticsService.getSummarization({
                          tasks,
                          sentenceCount,
                          query,
                          documents: [{ id: getId(), text: document, language: documentLanguage }],
                          pollingCallHandler: res => {
                              postValidate(res);

                              const extractiveAndAbstractiveTasks = res.tasks as NewSummarizationTaskSwagger<SummarizationSwagger>;

                              const abstractiveTask = extractiveAndAbstractiveTasks?.items?.find(t =>
                                  t.kind.includes('AbstractiveSummarization')
                              );
                              const extractiveTask = extractiveAndAbstractiveTasks?.items?.find(t =>
                                  t.kind.includes('ExtractiveSummarization')
                              );

                              if (extractiveTask?.results.errors?.length) {
                                  throw extractiveTask.results.errors[0].error.innererror ?? extractiveTask.results.errors[0].error;
                              }
                              if (abstractiveTask?.results.errors?.length) {
                                  throw abstractiveTask.results.errors[0].error.innererror ?? extractiveTask.results.errors[0].error;
                              }

                              if (extractiveTask?.status || abstractiveTask?.status) {
                                  setSummarization({
                                      extractive: { ...extractiveTask?.results, status: extractiveTask?.status },
                                      abstractive: { ...abstractiveTask?.results, status: abstractiveTask?.status },
                                      summarizationDemoOutput: [...extractiveAndAbstractiveTasks?.items]
                                  });
                              }
                          }
                      });
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    return {
        getPII,
        tracker,
        piiResult,
        sentiment,
        keyPhrases,
        resetResults,
        getSentiment,
        namedEntities,
        getKeyPhrases,
        summarization,
        getCurlCommand,
        linkedEntities,
        detectLanguage,
        healthAnalysis,
        getNamedEntities,
        getSummarization,
        getHealthAnalysis,
        detectedLanguages,
        getLinkedEntities,
        getSummarizationCurlCommand
    };
};
